/**
 * Enhanced Listening Animation - Research-Based Active Listening
 * Based on behavioral psychology: strong engagement, frequent micro-nods, direct eye contact
 * Shows genuine interest and encourages speaker to continue
 * Nuamedia Solutions Pty Ltd
 */
import * as THREE from 'three';
import React from 'react';
import { animationController } from './UnifiedAnimationController';

export interface ListeningAnimationRefs {
  headRef: React.RefObject<THREE.Object3D>;
  neckRef: React.RefObject<THREE.Object3D>;
  leftEyeRef?: React.RefObject<THREE.Object3D>;
  rightEyeRef?: React.RefObject<THREE.Object3D>;
}

/** Optional config from the avatar editor's Animation panel */
export interface ListeningAnimationConfig {
  nodFrequency?: number;  // nods per ~10s (default 3)
  tiltAmount?: number;    // head tilt amount 0-1 (default 0.045)
  suppressHeadMovement?: boolean;
}

export function setupListeningAnimation(
  headRef: React.RefObject<THREE.Object3D>,
  neckRef: React.RefObject<THREE.Object3D>,
  leftEyeRef?: React.RefObject<THREE.Object3D>,
  rightEyeRef?: React.RefObject<THREE.Object3D>,
  config?: ListeningAnimationConfig
) {
  if (!headRef?.current || !neckRef?.current) {
    console.warn('Missing refs for listening animation');
    return null;
  }

  let animationFrameId: number | null = null;
  let resetAnimationFrameId: number | null = null; // Track reset animation to prevent orphaned loops
  let transitionAnimationFrameId: number | null = null; // Track active pose transitions so speaking can cancel them
  let startTime = Date.now();
  let lastNodTime = Date.now();
  // nodFrequency: nods per ~10s. Default 3 = gentler ~3.3s between nods
  const nodInterval = Math.max(1200, 10000 / Math.max(1, config?.nodFrequency ?? 3));
  let nextNodTime = Date.now() + nodInterval * 0.5 + Math.random() * nodInterval * 0.5;
  let lastSaccadeTime = Date.now();
  let isCancelled = false; // Flag for immediate cancellation

  // Capture ACTUAL current positions when animation starts (prevents jumping on first activation)
  const initialPositions = {
    headY: headRef.current.rotation.y || 0,
    headZ: headRef.current.rotation.z || 0,
    headX: headRef.current.rotation.x || 0,
    neckY: neckRef.current.rotation.y || 0,
    neckZ: neckRef.current.rotation.z || 0,
    neckX: neckRef.current.rotation.x || 0,
    leftEyeY: leftEyeRef?.current?.rotation.y || 0,
    leftEyeX: leftEyeRef?.current?.rotation.x || 0,
    rightEyeY: rightEyeRef?.current?.rotation.y || 0,
    rightEyeX: rightEyeRef?.current?.rotation.x || 0
  };
  const hasExistingHeadTurn = Math.abs(initialPositions.headY) > 0.04;
  const hasExistingHeadTilt = Math.abs(initialPositions.headZ) > 0.03;
  const hasExistingHeadPitch = Math.abs(initialPositions.headX) > 0.025;

  const cancelTrackedFrames = () => {
    if (animationFrameId) {
      animationController.untrackRafId(animationFrameId);
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    if (resetAnimationFrameId) {
      animationController.untrackRafId(resetAnimationFrameId);
      cancelAnimationFrame(resetAnimationFrameId);
      resetAnimationFrameId = null;
    }

    if (transitionAnimationFrameId) {
      animationController.untrackRafId(transitionAnimationFrameId);
      cancelAnimationFrame(transitionAnimationFrameId);
      transitionAnimationFrameId = null;
    }
  };

  const animate = () => {
    // Check if cancelled (immediate speaking takeover)
    if (isCancelled || animationController.isSpeaking()) {
      console.log('👂 Listening animation cancelled - speaking active');
      return;
    }

    if (!headRef?.current || !neckRef?.current) return;

    const now = Date.now();
    const elapsed = (now - startTime) / 1000;
    const progress = Math.min(elapsed / 0.5, 1);

    const suppressHeadMovement = config?.suppressHeadMovement === true;

    // SUBTLE LISTENING POSE - Keep looking forward with minimal movement
    // User feedback: head should look forward, only slight subtle movements
    // Respect an existing pose so listening does not keep stacking more tilt/lean.
    const targetHeadRotation = suppressHeadMovement || hasExistingHeadTurn ? 0 : -0.015;   // Very subtle turn (almost none - keep looking forward)
    const targetNeckRotation = suppressHeadMovement || hasExistingHeadTurn ? 0 : -0.008;   // Minimal neck movement
    const targetEarTilt = suppressHeadMovement || hasExistingHeadTilt ? 0 : 0.02;          // Very subtle tilt (barely noticeable)
    const targetHeadForward = suppressHeadMovement || hasExistingHeadPitch ? 0 : 0.008;    // Minimal forward lean

    const easeProgress = progress < 0.5
      ? 2 * progress * progress
      : -1 + (4 - 2 * progress) * progress;

    const additionalHeadY = targetHeadRotation * easeProgress;
    const additionalNeckY = targetNeckRotation * easeProgress;
    const additionalHeadZ = targetEarTilt * easeProgress;
    const additionalHeadX = targetHeadForward * easeProgress;

    // Apply base listening pose
    headRef.current.rotation.y = initialPositions.headY + additionalHeadY;
    headRef.current.rotation.z = initialPositions.headZ + additionalHeadZ;
    headRef.current.rotation.x = initialPositions.headX + additionalHeadX;
    neckRef.current.rotation.y = initialPositions.neckY + additionalNeckY;
    neckRef.current.rotation.z = (initialPositions.neckZ || 0) + additionalHeadZ * 0.5;

    // Occasional micro-nods - attentive without feeling impatient
    const timeSinceLastNod = now - lastNodTime;
    const nodDuration = 500;  // slower complete nod (down and back up)

    // Check if it's time to start a new nod
    if (now >= nextNodTime && timeSinceLastNod > nodDuration) {
      lastNodTime = now;
      nextNodTime = now + nodInterval * 0.7 + Math.random() * nodInterval * 0.6;
      console.log('👂 Starting new listening nod');
    }

    // Apply nod only during the nod duration window
    if (timeSinceLastNod <= nodDuration) {
      const nodProgress = timeSinceLastNod / nodDuration;
      // tiltAmount: 0-1, maps to nod amplitude 0.003-0.03 by default
      const nodAmplitude = suppressHeadMovement || hasExistingHeadPitch ? 0 : 0.003 + (config?.tiltAmount ?? 0.045) * 0.35;
      // Sine wave for smooth down-and-up motion (0 -> peak -> 0)
      const nodOffset = Math.sin(nodProgress * Math.PI) * nodAmplitude * easeProgress;

      if (headRef.current.rotation) {
        // Apply nod on top of base head position (positive = nod down)
        headRef.current.rotation.x = initialPositions.headX + additionalHeadX + nodOffset;
      }
    }

    // MINIMAL sway - keep head very still (subtle listening, not swaying)
    const swayAmount = suppressHeadMovement ? 0 : Math.sin(elapsed * 0.3) * 0.001;  // Almost imperceptible sway
    headRef.current.rotation.y += swayAmount;

    // Eye compensation - eyes counter-rotate to maintain gaze toward speaker
    if (leftEyeRef?.current && rightEyeRef?.current) {
      // Maintain eye contact while head tilts
      const eyeCompensationY = -additionalHeadY * 0.85;
      const eyeCompensationZ = -additionalHeadZ * 0.3;  // Compensate for tilt

      leftEyeRef.current.rotation.y = initialPositions.leftEyeY + eyeCompensationY;
      leftEyeRef.current.rotation.x = initialPositions.leftEyeX + eyeCompensationZ;
      rightEyeRef.current.rotation.y = initialPositions.rightEyeY + eyeCompensationY;
      rightEyeRef.current.rotation.x = initialPositions.rightEyeX + eyeCompensationZ;

      // INFREQUENT eye saccades - active listeners maintain eye contact (not wandering)
      if (now - lastSaccadeTime > 4000 + Math.random() * 2000) { // Every 4-6 seconds
        lastSaccadeTime = now;

        // Brief saccades (eyes briefly look at different parts of speaker's face)
        const saccadeAmplitudeX = (Math.random() - 0.5) * 0.04;  // Smaller movements
        const saccadeAmplitudeY = (Math.random() - 0.3) * 0.03;  // Smaller movements

        // Quick saccade (humans move eyes 300-700 degrees/sec)
        const saccadeDuration = 60 + Math.random() * 50; // 60-110ms (realistic)
        const saccadeProgress = Math.min(((now % saccadeDuration) / saccadeDuration), 1);

        if (saccadeProgress < 0.4) { // Quick movement phase
          const saccadeEase = saccadeProgress < 0.2 ? saccadeProgress * 5 : (0.4 - saccadeProgress) * 5;
          leftEyeRef.current.rotation.y += saccadeAmplitudeX * saccadeEase;
          leftEyeRef.current.rotation.x += saccadeAmplitudeY * saccadeEase;
          rightEyeRef.current.rotation.y += saccadeAmplitudeX * saccadeEase * 0.98; // Slight asymmetry
          rightEyeRef.current.rotation.x += saccadeAmplitudeY * saccadeEase * 1.02;
        }
      }

      // Subtle eye contact focus (slight convergence - natural listening)
      const focusAmount = 0.008 * easeProgress;  // Reduced for subtle look
      leftEyeRef.current.rotation.y += focusAmount;
      rightEyeRef.current.rotation.y -= focusAmount;
    }

    animationFrameId = requestAnimationFrame(animate);
    // Track RAF ID with controller for immediate cancellation
    if (animationFrameId) {
      animationController.trackRafId(animationFrameId);
    }
  };

  const cleanup = () => {
    cancelTrackedFrames();

    const resetAnimation = () => {
      // Check if refs still exist
      if (!headRef?.current || !neckRef?.current) {
        console.log('Refs no longer available during cleanup');
        return;
      }

      const stillMoving =
        Math.abs(headRef.current.rotation.y - initialPositions.headY) > 0.001 ||
        Math.abs(headRef.current.rotation.z - initialPositions.headZ) > 0.001 ||
        Math.abs(headRef.current.rotation.x - initialPositions.headX) > 0.001 ||
        Math.abs(neckRef.current.rotation.y - initialPositions.neckY) > 0.001 ||
        Math.abs(neckRef.current.rotation.x - initialPositions.neckX) > 0.001 ||
        Math.abs(neckRef.current.rotation.z - (initialPositions.neckZ || 0)) > 0.001 ||
        (leftEyeRef?.current && Math.abs(leftEyeRef.current.rotation.y - initialPositions.leftEyeY) > 0.001) ||
        (leftEyeRef?.current && Math.abs(leftEyeRef.current.rotation.x - initialPositions.leftEyeX) > 0.001) ||
        (rightEyeRef?.current && Math.abs(rightEyeRef.current.rotation.y - initialPositions.rightEyeY) > 0.001) ||
        (rightEyeRef?.current && Math.abs(rightEyeRef.current.rotation.x - initialPositions.rightEyeX) > 0.001);

      if (stillMoving) {
        // Smoothly reset ALL positions back to initial
        headRef.current.rotation.y = THREE.MathUtils.lerp(
          headRef.current.rotation.y,
          initialPositions.headY,
          0.15  // Faster reset
        );
        headRef.current.rotation.z = THREE.MathUtils.lerp(
          headRef.current.rotation.z,
          initialPositions.headZ,
          0.15
        );
        headRef.current.rotation.x = THREE.MathUtils.lerp(
          headRef.current.rotation.x,
          initialPositions.headX,
          0.15
        );

        neckRef.current.rotation.x = THREE.MathUtils.lerp(
          neckRef.current.rotation.x,
          initialPositions.neckX,
          0.15
        );
        neckRef.current.rotation.y = THREE.MathUtils.lerp(
          neckRef.current.rotation.y,
          initialPositions.neckY,
          0.15
        );
        neckRef.current.rotation.z = THREE.MathUtils.lerp(
          neckRef.current.rotation.z,
          initialPositions.neckZ || 0,
          0.15
        );

        if (leftEyeRef?.current) {
          leftEyeRef.current.rotation.y = THREE.MathUtils.lerp(
            leftEyeRef.current.rotation.y,
            initialPositions.leftEyeY,
            0.15
          );
          leftEyeRef.current.rotation.x = THREE.MathUtils.lerp(
            leftEyeRef.current.rotation.x,
            initialPositions.leftEyeX,
            0.15
          );
        }
        if (rightEyeRef?.current) {
          rightEyeRef.current.rotation.y = THREE.MathUtils.lerp(
            rightEyeRef.current.rotation.y,
            initialPositions.rightEyeY,
            0.15
          );
          rightEyeRef.current.rotation.x = THREE.MathUtils.lerp(
            rightEyeRef.current.rotation.x,
            initialPositions.rightEyeX,
            0.15
          );
        }

        resetAnimationFrameId = requestAnimationFrame(resetAnimation);
        if (resetAnimationFrameId) {
          animationController.trackRafId(resetAnimationFrameId);
        }
      } else {
        // Force exact reset on final frame - CORRECT ORDER: x, y, z
        resetAnimationFrameId = null; // Clear the ID when done
        headRef.current.rotation.x = initialPositions.headX;
        headRef.current.rotation.y = initialPositions.headY;
        headRef.current.rotation.z = initialPositions.headZ;

        neckRef.current.rotation.x = initialPositions.neckX;  // Fixed: was 0, should be initial
        neckRef.current.rotation.y = initialPositions.neckY;
        neckRef.current.rotation.z = initialPositions.neckZ || 0;

        if (leftEyeRef?.current) {
          leftEyeRef.current.rotation.x = initialPositions.leftEyeX;
          leftEyeRef.current.rotation.y = initialPositions.leftEyeY;
          leftEyeRef.current.rotation.z = 0;
        }
        if (rightEyeRef?.current) {
          rightEyeRef.current.rotation.x = initialPositions.rightEyeX;
          rightEyeRef.current.rotation.y = initialPositions.rightEyeY;
          rightEyeRef.current.rotation.z = 0;
        }
        console.debug('Listening animation reset');
      }
    };

    resetAnimation();
  };

  const forceImmediateReset = () => {
    // IMMEDIATE synchronous reset (no lerp, no requestAnimationFrame)
    // Used when lip-sync is about to start to prevent interference
    isCancelled = true; // Set flag to prevent any new RAF callbacks
    cancelTrackedFrames();

    if (headRef?.current) {
      headRef.current.rotation.x = initialPositions.headX;
      headRef.current.rotation.y = initialPositions.headY;
      headRef.current.rotation.z = initialPositions.headZ;
    }

    if (neckRef?.current) {
      neckRef.current.rotation.x = initialPositions.neckX;
      neckRef.current.rotation.y = initialPositions.neckY;
      neckRef.current.rotation.z = initialPositions.neckZ || 0;
    }

    if (leftEyeRef?.current) {
      leftEyeRef.current.rotation.x = initialPositions.leftEyeX;
      leftEyeRef.current.rotation.y = initialPositions.leftEyeY;
      leftEyeRef.current.rotation.z = 0;
    }

    if (rightEyeRef?.current) {
      rightEyeRef.current.rotation.x = initialPositions.rightEyeX;
      rightEyeRef.current.rotation.y = initialPositions.rightEyeY;
      rightEyeRef.current.rotation.z = 0;
    }

    console.log('Listening animation FORCE reset (immediate, no lerp)');
  };

  const cancelAndHoldPose = () => {
    isCancelled = true;
    cancelTrackedFrames();
    console.log('Listening animation cancelled while preserving current pose');
  };

  /**
   * Smooth transition to target positions (for blending with other animations)
   * @param targetPositions Target rotation positions
   * @param duration Transition duration in ms (default 300ms)
   */
  const transitionTo = (targetPositions: {
    headX?: number;
    headY?: number;
    headZ?: number;
    neckX?: number;
    neckY?: number;
    neckZ?: number;
    leftEyeX?: number;
    leftEyeY?: number;
    rightEyeX?: number;
    rightEyeY?: number;
  }, duration: number = 300) => {
    if (!headRef?.current || !neckRef?.current) return;

    // Capture current positions as starting point
    const startPositions = {
      headX: headRef.current.rotation.x,
      headY: headRef.current.rotation.y,
      headZ: headRef.current.rotation.z,
      neckX: neckRef.current.rotation.x,
      neckY: neckRef.current.rotation.y,
      neckZ: neckRef.current.rotation.z || 0,
      leftEyeX: leftEyeRef?.current?.rotation.x || 0,
      leftEyeY: leftEyeRef?.current?.rotation.y || 0,
      rightEyeX: rightEyeRef?.current?.rotation.x || 0,
      rightEyeY: rightEyeRef?.current?.rotation.y || 0
    };

    const transitionStartTime = Date.now();

    const transitionAnimate = () => {
      const elapsed = Date.now() - transitionStartTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress; // Ease in-out

      if (headRef?.current) {
        if (targetPositions.headX !== undefined) {
          headRef.current.rotation.x = THREE.MathUtils.lerp(startPositions.headX, targetPositions.headX, easedProgress);
        }
        if (targetPositions.headY !== undefined) {
          headRef.current.rotation.y = THREE.MathUtils.lerp(startPositions.headY, targetPositions.headY, easedProgress);
        }
        if (targetPositions.headZ !== undefined) {
          headRef.current.rotation.z = THREE.MathUtils.lerp(startPositions.headZ, targetPositions.headZ, easedProgress);
        }
      }

      if (neckRef?.current) {
        if (targetPositions.neckX !== undefined) {
          neckRef.current.rotation.x = THREE.MathUtils.lerp(startPositions.neckX, targetPositions.neckX, easedProgress);
        }
        if (targetPositions.neckY !== undefined) {
          neckRef.current.rotation.y = THREE.MathUtils.lerp(startPositions.neckY, targetPositions.neckY, easedProgress);
        }
        if (targetPositions.neckZ !== undefined) {
          neckRef.current.rotation.z = THREE.MathUtils.lerp(startPositions.neckZ, targetPositions.neckZ, easedProgress);
        }
      }

      if (leftEyeRef?.current) {
        if (targetPositions.leftEyeX !== undefined) {
          leftEyeRef.current.rotation.x = THREE.MathUtils.lerp(startPositions.leftEyeX, targetPositions.leftEyeX, easedProgress);
        }
        if (targetPositions.leftEyeY !== undefined) {
          leftEyeRef.current.rotation.y = THREE.MathUtils.lerp(startPositions.leftEyeY, targetPositions.leftEyeY, easedProgress);
        }
      }

      if (rightEyeRef?.current) {
        if (targetPositions.rightEyeX !== undefined) {
          rightEyeRef.current.rotation.x = THREE.MathUtils.lerp(startPositions.rightEyeX, targetPositions.rightEyeX, easedProgress);
        }
        if (targetPositions.rightEyeY !== undefined) {
          rightEyeRef.current.rotation.y = THREE.MathUtils.lerp(startPositions.rightEyeY, targetPositions.rightEyeY, easedProgress);
        }
      }

      if (progress < 1) {
        transitionAnimationFrameId = requestAnimationFrame(transitionAnimate);
        if (transitionAnimationFrameId) {
          animationController.trackRafId(transitionAnimationFrameId);
        }
      } else {
        if (transitionAnimationFrameId) {
          animationController.untrackRafId(transitionAnimationFrameId);
          transitionAnimationFrameId = null;
        }
      }
    };

    // Stop current animation during transition
    if (animationFrameId) {
      animationController.untrackRafId(animationFrameId);
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    if (transitionAnimationFrameId) {
      animationController.untrackRafId(transitionAnimationFrameId);
      cancelAnimationFrame(transitionAnimationFrameId);
      transitionAnimationFrameId = null;
    }

    transitionAnimationFrameId = requestAnimationFrame(transitionAnimate);
    if (transitionAnimationFrameId) {
      animationController.trackRafId(transitionAnimationFrameId);
    }

    // Return cleanup function
    return () => {
      if (transitionAnimationFrameId) {
        animationController.untrackRafId(transitionAnimationFrameId);
        cancelAnimationFrame(transitionAnimationFrameId);
        transitionAnimationFrameId = null;
      }
    };
  };

  /**
   * Get current animation positions (for smooth transitions)
   */
  const getCurrentPositions = () => {
    return {
      headX: headRef?.current?.rotation.x || 0,
      headY: headRef?.current?.rotation.y || 0,
      headZ: headRef?.current?.rotation.z || 0,
      neckX: neckRef?.current?.rotation.x || 0,
      neckY: neckRef?.current?.rotation.y || 0,
      neckZ: neckRef?.current?.rotation.z || 0,
      leftEyeX: leftEyeRef?.current?.rotation.x || 0,
      leftEyeY: leftEyeRef?.current?.rotation.y || 0,
      rightEyeX: rightEyeRef?.current?.rotation.x || 0,
      rightEyeY: rightEyeRef?.current?.rotation.y || 0
    };
  };

  return {
    start: () => {
      // Don't start if speaking is active
      if (animationController.isSpeaking()) {
        console.log('👂 LISTENING: Cannot start - speaking is active');
        return;
      }

      console.log('👂 LISTENING: Subtle pose - minimal head turn, gentle nods, soft eye focus');
      isCancelled = false; // Reset cancelled flag
      startTime = Date.now();
      lastNodTime = Date.now();
      nextNodTime = Date.now() + 3500 + Math.random() * 1500;  // Schedule first nod
      lastSaccadeTime = Date.now();
      animate();
    },
    stop: cleanup,
    cleanup,
    cancelAndHoldPose,
    forceImmediateReset,  // Export for external use (voice handlers)
    transitionTo,         // Smooth transition to target positions
    getCurrentPositions    // Get current positions for blending
  };
}


